<script setup>
/**
 * @todo When the legacy theme is removed: delete legacyProps, useTheme, and the q-breadcrumbs
 * branch. The M3 branch can then be the single render path.
 */

import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';
import HrIcon from './HrIcon.vue';

/**
 * Navigation breadcrumb trail with optional links and auto-rendered separators.
 *
 * An item goes somewhere through `to`, a route, or through `handler` when moving up the trail
 * changes what the page shows without changing the route, such as the open folder in a library.
 *
 * @status recommended
 * @group navigation
 * @useWhen Showing the user's current location within a multi-level navigation hierarchy
 * @a11y Linked items are links and handled items are buttons, so both are reached with Tab and
 *   activated with Enter; the last item is marked as the current page.
 * @see https://quasar.dev/vue-components/breadcrumbs Quasar q-breadcrumbs API
 */
defineOptions({ name: 'HrBreadcrumbs' });

/**
 * @typedef {{ label: string, to?: string | object | null, handler?: (() => void) | null }} HrBreadcrumbItem
 */

const props = defineProps({
  /**
   * Ordered list of breadcrumb items. Items with a `to` value render as links, and items with a
   * `handler` as buttons that run it; `to` wins if both are given. The last item (current page)
   * typically has neither.
   * @type {HrBreadcrumbItem[]}
   * @example [{"label":"Library","to":"/library"},{"label":"Early learners"}]
   */
  items: {
    type: Array,
    default: () => ([]),
  },
  /**
   * Legacy props forwarded to q-breadcrumbs when the legacy theme is active. Ignored under the
   * design-system theme.
   * @type {Object} legacyProps
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => {
  if (hasTheme.value) {
    return { ...attrs };
  }
  return { ...props.legacyProps, ...attrs };
});

const isHandled = (item) => item.to == null && item.handler != null;
</script>

<template>
  <!-- M3 theme: own container — no Quasar breadcrumb coupling needed -->
  <nav
    v-if="hasTheme"
    aria-label="breadcrumb"
    class="hr-breadcrumbs"
    v-bind="attrs"
  >
    <template
      v-for="(item, i) in props.items"
      :key="i"
    >
      <hr-icon
        v-if="i > 0"
        name="chevron_right"
        size="sm"
        class="hr-breadcrumbs__separator"
        aria-hidden="true"
      />
      <router-link
        v-if="item.to != null"
        :to="item.to"
        class="hr-breadcrumbs__item hr-breadcrumbs__item--linked"
      >
        {{ item.label }}
      </router-link>
      <button
        v-else-if="item.handler != null"
        type="button"
        class="hr-breadcrumbs__item hr-breadcrumbs__item--linked hr-breadcrumbs__item--button"
        @click="item.handler"
      >
        {{ item.label }}
      </button>
      <span
        v-else
        class="hr-breadcrumbs__item hr-breadcrumbs__item--current"
        aria-current="page"
      >{{ item.label }}</span>
    </template>
  </nav>
  <!-- Legacy theme: Quasar handles separator injection natively -->
  <q-breadcrumbs
    v-else
    v-bind="effectiveProps"
  >
    <template #separator>
      <q-icon
        name="chevron_right"
        color="primary"
        size="1.5em"
      />
    </template>
    <q-breadcrumbs-el
      v-for="(item, i) in props.items"
      :key="i"
      :label="item.label"
      :to="item.to ?? undefined"
      :tag="isHandled(item) ? 'button' : undefined"
      :type="isHandled(item) ? 'button' : undefined"
      :class="isHandled(item) ? 'hr-breadcrumbs__legacy-button' : undefined"
      @click="isHandled(item) ? item.handler() : undefined"
    />
  </q-breadcrumbs>
</template>

<style lang="scss" scoped>
.hr-breadcrumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;

  font-size: var(--app-type-label-large-size);
  line-height: var(--app-type-label-large-line-height);
  font-weight: var(--app-type-label-large-weight);
  letter-spacing: var(--app-type-label-large-tracking);
}

.hr-breadcrumbs__separator {
  color: var(--app-on-surface-variant);
  flex-shrink: 0;
}

.hr-breadcrumbs__item {
  text-decoration: none;
}

.hr-breadcrumbs__item--linked {
  color: var(--app-primary);
  transition: opacity 150ms linear;

  &:hover { opacity: 0.88; }
}

.hr-breadcrumbs__item--button {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  letter-spacing: inherit;
  cursor: pointer;
}

.hr-breadcrumbs__legacy-button {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}

.hr-breadcrumbs__item--current {
  color: var(--app-on-surface);
}
</style>
